/**
 * Canonical Generation → user-facing Asset destination for Canvas delete
 * confirm copy and future shared lifecycle UI.
 *
 * Product rule (P0): Canvas Node ≠ Generated Asset. While a run is active,
 * deleting the node must tell the user which Asset library a successful
 * background result will land in — never technical model names.
 */
export type GenerationAssetKind = 'image' | 'video' | 'audio' | 'text' | 'unknown'

export type GenerationAssetDestination = {
  /** Internal capability / modality key (stable). */
  assetKind: GenerationAssetKind
  /** i18n key under `canvas.board.assetDestination.*` for 「图片资产」 etc. */
  assetLabelKey: string
  /** True when a tenant-facing Asset page/API already exists for this kind. */
  assetReady: boolean
  /** Optional SPA path fragment for docs/tests (not required by delete UX). */
  assetPageHint?: string
}

/** Map Canvas `nodeType` (and RF family aliases) → Asset destination. */
const BY_NODE_TYPE: Record<string, GenerationAssetDestination> = {
  generate_image: {
    assetKind: 'image',
    assetLabelKey: 'board.assetDestination.image',
    assetReady: true,
    assetPageHint: 'images',
  },
  generate_tryon: {
    assetKind: 'image',
    assetLabelKey: 'board.assetDestination.image',
    assetReady: true,
    assetPageHint: 'images',
  },
  generate_video: {
    assetKind: 'video',
    assetLabelKey: 'board.assetDestination.video',
    assetReady: true,
    assetPageHint: 'video-generation/assets',
  },
  generate_voice: {
    assetKind: 'audio',
    assetLabelKey: 'board.assetDestination.audio',
    // No dedicated tenant Audio Asset Center yet — delete copy must NOT
    // promise 「音频资产」; deleted-node success is not billable until ready.
    assetReady: false,
    assetPageHint: undefined,
  },
  generate_voice_template: {
    assetKind: 'audio',
    assetLabelKey: 'board.assetDestination.audio',
    assetReady: false,
    assetPageHint: undefined,
  },
  generate_text: {
    assetKind: 'text',
    assetLabelKey: 'board.assetDestination.text',
    assetReady: false,
  },
  script_generation: {
    assetKind: 'text',
    assetLabelKey: 'board.assetDestination.text',
    assetReady: false,
  },
  storyboard_generation: {
    assetKind: 'text',
    assetLabelKey: 'board.assetDestination.text',
    assetReady: false,
  },
  generate_creative: {
    assetKind: 'text',
    assetLabelKey: 'board.assetDestination.text',
    assetReady: false,
  },
  project_start: {
    assetKind: 'text',
    assetLabelKey: 'board.assetDestination.text',
    assetReady: false,
  },
}

/** RF `node.type` / family → same destinations (keyboard delete uses family). */
const BY_FAMILY: Record<string, GenerationAssetDestination> = {
  generator: BY_NODE_TYPE.generate_image,
  tryon_generator: BY_NODE_TYPE.generate_tryon,
  video_generator: BY_NODE_TYPE.generate_video,
  audio_generator: BY_NODE_TYPE.generate_voice,
  voice_generator: BY_NODE_TYPE.generate_voice_template,
  text_generator: BY_NODE_TYPE.generate_text,
  script_generation: BY_NODE_TYPE.script_generation,
  storyboard_generation: BY_NODE_TYPE.storyboard_generation,
  generate_creative: BY_NODE_TYPE.generate_creative,
}

const FALLBACK: GenerationAssetDestination = {
  assetKind: 'unknown',
  assetLabelKey: 'board.assetDestination.generic',
  assetReady: false,
}

export function resolveGenerationAssetDestination(args: {
  nodeType?: string | null
  family?: string | null
}): GenerationAssetDestination {
  const nodeType = (args.nodeType || '').trim()
  if (nodeType && BY_NODE_TYPE[nodeType]) return BY_NODE_TYPE[nodeType]
  const family = (args.family || '').trim()
  if (family && BY_FAMILY[family]) return BY_FAMILY[family]
  return FALLBACK
}

const ACTIVE_STATUS = new Set([
  'SUBMITTING',
  'QUEUED',
  'GENERATING',
  'WAITING_FOR_ENGINE',
  'RUNNING',
  'PENDING',
])

/** Detect in-flight generation on a Canvas node's fields (shared delete UX). */
export function nodeFieldsIndicateActiveGeneration(fields: Record<string, unknown> | null | undefined): boolean {
  if (!fields || typeof fields !== 'object') return false
  const statusKeys = ['status', 'runStatus', 'divergeRunStatus', 'taskStatus', 'jobStatus'] as const
  for (const key of statusKeys) {
    const v = String(fields[key] ?? '')
    if (ACTIVE_STATUS.has(v)) return true
  }
  return false
}
